import { CorporateEditForm } from 'application/corporate/forms';
import {BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import {CorporateService, CorporateContactService, ReportingIntervalTypeService} from 'application/corporate/services';
import { action, observable } from 'mobx';
import { ModalParams } from 'core/models';
import _ from 'lodash';

class CorporateEditViewStore extends BaseEditViewStore {
    @observable accessor contacts = null;
    @observable accessor facilities = null;
    
    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;
        const isNiko = !!(rootStore.routerStore.routerState.queryParams &&
            rootStore.routerStore.routerState.queryParams.isNiko);
        const service = new CorporateService(rootStore.application.baasic.apiClient);
        const reportingService = new ReportingIntervalTypeService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'role',
            id: id,
            Form: CorporateEditForm,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async (id, opts) => {
                        const response = await service.get(id, _.extend(opts, {embed:"Facilities,CorporateContacts"} ));
                        this.facilities = response.data.facilities;
                        this.contacts = response.data.corporateContacts;
                        return response.data;
                    },
                    create: async (resource) => {
                        const response = await service.create({ ...resource, isNiko });
                        return response.data;
                    },
                    update: async (resource) => {
                        const response = await service.update(resource);
                        return response.data;
                    }
                }
            },
            FormClass: CorporateEditForm,
        });
        this.isNiko = isNiko;
        this.corporateContactService = new CorporateContactService(rootStore.application.baasic.apiClient);

        this.contactEditModal = new ModalParams({ 
            onSuccess: this.onSuccessUpdateContact
        });

        this.reportingIntervalTypeStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async(term) => {
                const response = await reportingService.find({ searchQuery: term, rpp: 200 });
                return response.data.item;      
             }   
        });
    } 
    
    @action.bound
    onAddContact(contact) {
        this.contactEditModal.open({ 
            item: contact,
            facilities: this.facilities,
            contacts: this.contacts,
            onSuccess: this.onSuccessUpdateContact
        });
    }
    
    @action.bound
    async onDeleteContact(contact) {
        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            "Are you sure you want to delete the selected contact?",
            async () => {
                try {
                    await this.corporateContactService.delete(contact);
                    let result = await this.corporateContactService.find({rpp:100, sort:"dateCreated|desc", corporateId:this.id});
                    this.contacts = result.data.item;
                    await setTimeout(() => this.rootStore.notificationStore.success("Contact successfully removed."));
                } catch (err) {
                    notificationStore.error('An error ocurred while deleting the contact!', err);
                }
            }
        );
    }

    @action.bound
    async onSuccessUpdateContact() {     
        this.contactEditModal.close();
        let result = await this.corporateContactService.find({rpp:100, sort:"dateCreated|desc", corporateId:this.id, embed:"CorporateContactContactDepartments"});
        this.contacts = result.data.item.map(r => {return {...r, contactDepartments: r.corporateContactContactDepartments && r.corporateContactContactDepartments.map(cc => cc.contactDepartment ) }});
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.create(resource);

            this.form.setFieldsDisabled(false);

            // The Preview/Edit/Create pages are shared between BT and Niko corporates, so
            // redirect to the originating list explicitly (goBack would land on the BT list).
            this.rootStore.routerStore.goTo(
                this.isNiko ? 'master.app.main.niko-corporate.list' : 'master.app.main.corporate.list'
            );
            await setTimeout(() => this.notifySuccessCreate(), 10);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onCreateError(err);
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.update({
                id: this.id,
                ...resource
            });

            this.form.setFieldsDisabled(false);

            await this.rootStore.routerStore.goBack();
            this.rootStore.notificationStore.success("CORPORATE.EDIT.SUCCESS");

        }
        catch(err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }
}

export default CorporateEditViewStore;
